/**
 * One row inside a student's session card. The card owns the chrome; the
 * row is a social post. The type icon sits on the author line so the body
 * lines up with the checkbox instead of being pushed right.
 */
import { Checkbox } from "antd"
import { type ComponentProps, type CSSProperties, type ReactNode } from "react"
import styled from "styled-components"

/** Shared rows use the same checkbox, checked and green, so the row does not shift. */
const SharedCheck = styled.span`
  &.is-shared .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #1d8921;
    border-color: #1d8921;
  }
`

export const FeedShareCheckbox = ({
  testId,
  shared,
  checked,
  disabled = false,
  ariaLabel,
  onChange,
  dataAttrs,
}: {
  testId: string
  shared: boolean
  checked: boolean
  disabled?: boolean
  ariaLabel: string
  onChange: (checked: boolean) => void
  dataAttrs?: Record<string, string>
}) => (
  <SharedCheck
    data-testid={testId}
    data-shared={shared ? "true" : "false"}
    className={shared ? "is-shared" : undefined}
    {...dataAttrs}
  >
    <Checkbox
      aria-label={ariaLabel}
      checked={shared || checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
  </SharedCheck>
)

export const FEED_CARD_BORDER = "#E7D3B0"
export const FEED_DIVIDER = "#F0E6D8"
/** Green rail down the left of one student's session list. Matches theme.eric.green. */
export const FEED_RAIL = "#1D8921"
export const FEED_HEADER_BG = "#FFFBF6"
export const FEED_NOTE_ICON_BG = "#F8E6D4"
export const FEED_STORY_ICON_BG = "#EDE4F7"
export const FEED_SCAN_ICON_BG = "#E7F0E4"

export const feedPill = (background: string, color: string): CSSProperties => ({
  background,
  color,
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: 12,
  fontWeight: 600,
  lineHeight: "18px",
  whiteSpace: "nowrap",
})

export const FEED_ROLE_PILL = feedPill("#F2F2F2", "#555555")
export const FEED_STORY_PILL = feedPill("#EDE4F7", "#6D4A9A")
export const FEED_PENDING_PILL = feedPill("#F8E7C0", "#8A6A2F")
export const FEED_SHARED_PILL = feedPill("#E5F5E6", "#1D8921")
export const FEED_DONT_SHARE_PILL = feedPill("#FBE4E4", "#D44040")
export const FEED_REVIEW_PILL = feedPill("#FDE7C7", "#9A6B2F")
export const FEED_UNHEARD_PILL = feedPill("#F3E8FF", "#6D4A9A")
/** Whole shared row. Same green as the shared pill, so a mixed card stays readable. */
export const FEED_SHARED_BG = "#E5F5E6"

/** Small type mark on the author line. Not a column, so the body stays left. */
export const FeedTypeIcon = ({
  background,
  children,
}: {
  background: string
  children: ReactNode
}) => (
  <span
    style={{
      width: 22,
      height: 22,
      borderRadius: 6,
      background,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "0 0 auto",
      fontSize: 12,
    }}
  >
    {children}
  </span>
)

const StandoutFeedRow = ({
  testId,
  rowProps,
  selection,
  shared = false,
  children,
}: {
  testId: string
  rowProps?: Omit<ComponentProps<"div">, "style" | "children">
  selection?: ReactNode
  shared?: boolean
  children: ReactNode
}) => {
  const background = shared ? FEED_SHARED_BG : "#fff"
  return (
    <div
      {...rowProps}
      data-testid={testId}
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        padding: "14px 16px",
        borderTop: `1px solid ${FEED_DIVIDER}`,
        background,
      }}
    >
      {selection}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        {children}
      </div>
    </div>
  )
}

export default StandoutFeedRow
